import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60s — VPN tunnels can take up to ~30s on first connect
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── In-flight GET de-duplication ────────────────────────────────────────────
// A single page load was firing the SAME GET many times (StrictMode double-invoke
// + effect re-runs + the 503 retry + shared components like ImageSelector all
// hitting /registry/images), so e.g. /registry/images fired ~11× and the slow
// /app-services/?live=true fired repeatedly — piling up in-flight requests and
// leaving the table empty until they settled. We collapse concurrent IDENTICAL GETs
// (same URL + params) into ONE shared promise: the first fires the network call, the
// rest await its result. Cleared when the request settles, so later loads still work.
// Only GETs (safe/idempotent) are deduped — never mutations.
const _inflightGets = new Map();

function _getKey(url, config) {
  let p = '';
  try { p = JSON.stringify(config?.params || {}); } catch { p = ''; }
  return `${url}?${p}`;
}

function _dedupGet(originalGet) {
  return (url, config) => {
    // Opt-out for callers that explicitly want a fresh call.
    if (config && config._noDedup) return originalGet(url, config);
    const key = _getKey(url, config);
    const existing = _inflightGets.get(key);
    if (existing) return existing;
    const promise = originalGet(url, config).finally(() => {
      _inflightGets.delete(key);
    });
    _inflightGets.set(key, promise);
    return promise;
  };
}

// Token refresh state — prevent multiple concurrent refresh attempts
let _refreshPromise = null;

async function _refreshToken() {
  // Reuse in-flight refresh if one is already running
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.username || !user._pw) return null;
      const resp = await axios.post(
        `${API_URL}/auth/login`,
        new URLSearchParams({ username: user.username, password: user._pw }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      const newToken = resp.data.access_token;
      localStorage.setItem('token', newToken);
      return newToken;
    } catch {
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// Handle API errors:
//   401 → try silent token refresh once, else redirect to login
//   503/502/504 → retry once after 2s (pod restart window)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const config = error.config;

    // ── 401: try refresh once ────────────────────────────────────────────────
    if (status === 401 && !config._retried) {
      config._retried = true;
      const newToken = await _refreshToken();
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
        return api(config);
      }
      // Refresh failed — clear auth and go to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedClusterId');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    // ── 503 / 502 / 504: API pod restarting — retry once after 2s ───────────
    if ([502, 503, 504].includes(status) && !config._retried503) {
      config._retried503 = true;
      await new Promise((r) => setTimeout(r, 2000));
      return api(config);
    }

    return Promise.reject(error);
  }
);

// Wrap api.get with in-flight de-duplication (see _dedupGet above). Bound to the
// instance so `this` + interceptors still apply. Mutations (post/patch/delete) are
// intentionally NOT deduped.
const _origGet = api.get.bind(api);
api.get = _dedupGet(_origGet);

// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', new URLSearchParams({ username, password }), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }),
  verifyOtp: (otp_token, otp_code) =>
    api.post('/auth/verify-otp', { otp_token, otp_code }),
  resendOtp: (otp_token) =>
    api.post('/auth/resend-otp', { otp_token }),
  // Fresh effective permissions for the logged-in session (same shape as
  // login, minus the token) — used to pick up admin permission changes
  // without requiring the user to log out and back in.
  me: () => api.get('/auth/me'),
};

// Users API (superadmin only)
export const usersAPI = {
  me: () => api.get('/users/me'),
  list: (orgId) => api.get('/users/', { params: orgId ? { organization_id: orgId } : {} }),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  assignPermissions: (id, payload) => api.put(`/users/${id}/permissions`, Array.isArray(payload) ? { sections: payload } : payload),
  assignClusters: (id, cluster_ids) => api.put(`/users/${id}/clusters`, { cluster_ids }),
  assignServers: (id, serverIds) => api.put(`/users/${id}/servers`, { server_ids: serverIds }),
  // Whether this user's stored Rancher token still works, per cluster. Returns no token
  // material. A token can be deleted inside Rancher while the mapping row still looks
  // fine, so `healthy:false` is the only way to spot it before the user hits a 502.
  rancherTokenHealth: (id) => api.get(`/users/${id}/rancher-token`),
  setRancherToken: (id, data) => api.put(`/users/${id}/rancher-token`, data),
};

// Organizations API (superadmin only)
export const organizationsAPI = {
  list:   ()         => api.get('/organizations/'),
  get:    (id)       => api.get(`/organizations/${id}`),
  create: (data)     => api.post('/organizations/', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id)       => api.delete(`/organizations/${id}`),
  stats:  (id)       => api.get(`/organizations/${id}/stats`),
  getSections: (id)            => api.get(`/organizations/${id}/sections`),
  getGpuServers: (id)          => api.get(`/organizations/${id}/gpu-servers`),
  setGpuServers: (id, gpu_server_ids) => api.put(`/organizations/${id}/gpu-servers`, { gpu_server_ids }),
  setSections: (id, payload)   => api.put(`/organizations/${id}/sections`, Array.isArray(payload) ? { sections: payload } : payload),
};

// Internal container registry (Phase 1 list + Phase 2 build)
export const registryAPI = {
  listImages: () => api.get('/registry/images'),
  info: () => api.get('/registry/info'),
  // Phase 2 — build an image from a pasted Dockerfile (Kaniko Job on the cluster)
  build: (data) => api.post('/registry/build', data),
  buildStatus: (name, clusterId) => api.get(`/registry/build/${name}/status`, { params: { cluster_id: clusterId } }),
  buildLogs: (name, clusterId, tail = 200) => api.get(`/registry/build/${name}/logs`, { params: { cluster_id: clusterId, tail } }),
};

// App Catalog API — deploy open-source apps / managed DB clusters via Helm
export const catalogAPI = {
  apps: () => api.get('/catalog/apps'),
  nodeRoles: (clusterId) => api.get('/catalog/node-roles', { params: { cluster_id: clusterId } }),
  releases: (clusterId) => api.get('/catalog/releases', { params: { cluster_id: clusterId } }),
  deploy: (data) => api.post('/catalog/deploy', data),
  connection: (releaseName, clusterId) =>
    api.get(`/catalog/releases/${releaseName}/connection`, { params: { cluster_id: clusterId } }),
  uninstall: (releaseName, clusterId) =>
    api.delete(`/catalog/releases/${releaseName}`, { params: { cluster_id: clusterId } }),
  setKeystore: (releaseName, clusterId, accessKey, secretKey) =>
    api.post(`/catalog/releases/${releaseName}/keystore`,
      { access_key: accessKey, secret_key: secretKey }, { params: { cluster_id: clusterId } }),
  resetKibanaAuth: (releaseName, clusterId) =>
    api.post(`/catalog/releases/${releaseName}/kibana/reset-auth`, null, { params: { cluster_id: clusterId } }),
};

// Servers API
export const serversAPI = {
  list: (params) => api.get('/servers/', { params }),
  get: (id) => api.get(`/servers/${id}`),
  create: (data) => api.post('/servers/', data),
  update: (id, data) => api.put(`/servers/${id}`, data),
  delete: (id) => api.delete(`/servers/${id}`),
  listSegments: () => api.get('/servers/segments'),
  testSSH: (id) => api.post(`/servers/${id}/test-ssh`, null, { timeout: 20000 }),
  generateAgentToken: (id, scope) => api.post(`/servers/${id}/generate-agent-token`, null, { params: { scope } }),
  updateAgentScope: (id, scope) => api.post(`/servers/${id}/agent-scope`, null, { params: { scope } }),
  unregisteredIPs: () => api.get('/servers/unregistered-ips'),
};

// Clusters API
export const clustersAPI = {
  list: () => api.get('/clusters/'),
  get: (id) => api.get(`/clusters/${id}`),
  create: (data) => api.post('/clusters/', data),
  update: (id, data) => api.put(`/clusters/${id}`, data),
  assignOrganization: (id, organizationId) => api.put(`/clusters/${id}/organization`, { organization_id: organizationId }),
  delete: (id) => api.delete(`/clusters/${id}`),
  provision: (data) => api.post('/clusters/provision', data),
  importCluster: (data) => api.post('/clusters/import', data),
  // Self-service Rancher registration — no rancher_server_url/rancher_cluster_id/
  // rancher_token from the caller; SarvKube creates the Rancher-side registration
  // itself (server-level admin credential) and returns the kubectl command to run.
  registerWithRancher: (data) => api.post('/clusters/rancher/register', data),
  getRancherRegistrationStatus: (clusterId) => api.get(`/clusters/${clusterId}/rancher-registration-status`),
  getRancherAdminTokenStatus: () => api.get('/clusters/rancher/admin-token-status'),
  testConnection: (data) => api.post('/clusters/test-connection', data),
  getResources: (id) => api.get(`/clusters/${id}/resources`),
  getStorageHealth: (id) => api.get(`/clusters/${id}/storage-health`),
  installLocalPathProvisioner: (id) => api.post(`/clusters/${id}/storage-health/install-local-path-provisioner`),
  // Access-credential self-heal: reuses a still-working per-user credential to
  // refresh the cluster-level one, entirely server-side — nobody types anything.
  // 409s when no working credential exists anywhere; there is deliberately no
  // fallback that asks a human for one (see app/services/rancher_token_repair.py).
  repairRancherToken: (id) => api.post(`/clusters/${id}/rancher-token/repair`),
  // ns (optional): scope the heavy list to one namespace (fast). Omit = all namespaces.
  getDeployments: (id, ns) => api.get(`/clusters/${id}/deployments`, { params: ns ? { namespace: ns } : {} }),
  getWorkloads: (id, ns) => api.get(`/clusters/${id}/workloads`, { params: ns ? { namespace: ns } : {} }),
  // Paged variants (Rancher/Steve only) — return {items,total,page,page_size,revision}.
  // One page costs the same regardless of collection size (50 of 1285 pods: 0.59s vs
  // 4.40s for the full list), so the big tables should use these. `total` is the FULL
  // collection size, which is what makes a numbered pager possible.
  getPodsPaged: (id, { page = 1, pageSize = 50, namespace, sort, name } = {}) =>
    api.get(`/clusters/${id}/pods/paged`, {
      params: { page, page_size: pageSize, ...(namespace ? { namespace } : {}), ...(sort ? { sort } : {}), ...(name ? { name } : {}) },
    }),
  getDeploymentsPaged: (id, { page = 1, pageSize = 50, namespace, sort } = {}) =>
    api.get(`/clusters/${id}/deployments/paged`, {
      params: { page, page_size: pageSize, ...(namespace ? { namespace } : {}), ...(sort ? { sort } : {}) },
    }),
  // Full live configuration for one object: resources, ports, env, volumes/PVCs,
  // conditions, events, related pods and raw YAML. Live from the cluster, so it shows
  // what is RUNNING — the Managed-by-SarvKube pages show what was requested.
  getPodDetail: (id, ns, name) => api.get(`/clusters/${id}/pods/${ns}/${name}/detail`),
  getDeploymentDetail: (id, ns, name) => api.get(`/clusters/${id}/deployments/${ns}/${name}/detail`),
  // Any workload kind: pods | deployments | statefulsets | daemonsets | jobs | cronjobs.
  // `resource` is a fixed path segment chosen by the calling page, never user input.
  // Apply an edited manifest back. One endpoint for every kind — kubectl apply does not
  // care what it is given, which is why this is YAML editing rather than a form per kind.
  applyWorkloadYaml: (resource, id, ns, name, yaml) =>
    api.put(`/clusters/${id}/${resource}/${encodeURIComponent(ns)}/${encodeURIComponent(name)}/apply`, { yaml }),

  getWorkloadDetail: (resource, id, ns, name) =>
    api.get(`/clusters/${id}/${resource}/${encodeURIComponent(ns)}/${encodeURIComponent(name)}/detail`),
  // Any workload kind with a delete endpoint: statefulsets | daemonsets | jobs | cronjobs.
  deleteWorkload: (resource, id, ns, name) =>
    api.delete(`/clusters/${id}/${resource}/${encodeURIComponent(ns)}/${encodeURIComponent(name)}`),
  // Steve-paged list for any workload kind. Replaces getWorkloads() for statefulsets,
  // daemonsets, jobs and cronjobs, which used to arrive together via the unpaged SDK path.
  getWorkloadPaged: (resource, id, { page = 1, pageSize = 50, namespace, sort } = {}) =>
    api.get(`/clusters/${id}/${resource}/paged`, {
      params: { page, page_size: pageSize, ...(namespace ? { namespace } : {}), ...(sort ? { sort } : {}) },
    }),

  // ── Namespace RBAC (Rancher Projects) ──────────────────────────────────────
  // Rancher owns every permission decision here; these just drive its /v3 API.
  // A namespace is visible to a non-admin only when all three line up: a Project
  // exists, the namespace is assigned to it, and the user is bound to it.
  // Just the namespace names. Cheaper than getResources(), which returns the whole cluster
  // snapshot (nodes, 1272 pods, ~240KB) when all a dropdown needs is a list of strings.
  listNamespaceNames: (id) => api.get(`/clusters/${id}/namespaces`),

  listProjects: (id) => api.get(`/clusters/${id}/projects`),
  createProject: (id, data) => api.post(`/clusters/${id}/projects`, data),
  // Returns {namespaces:[{name,project_id,assigned,stale,status}], summary:{...}}.
  // `stale` = annotated to ANOTHER cluster's project; Rancher ignores those, so they
  // behave as unassigned while looking configured. Shown separately for that reason.
  listNamespaceProjects: (id) => api.get(`/clusters/${id}/namespaces/projects`),
  setNamespaceProject: (id, ns, projectId) =>
    api.put(`/clusters/${id}/namespaces/${ns}/project`, { project_id: projectId }),
  // Bulk assign — each namespace can go to its OWN project in one call.
  // `assignments`: [{namespace, project_id}], project_id null = unassign.
  // Reports per-namespace failures instead of aborting the batch.
  bulkAssignNamespaces: (id, assignments) =>
    api.post(`/clusters/${id}/namespaces/assign`, { assignments }),
  listProjectBindings: (id, projectId) => api.get(`/clusters/${id}/projects/${projectId}/bindings`),
  addProjectBinding: (id, projectId, data) => api.post(`/clusters/${id}/projects/${projectId}/bindings`, data),
  removeProjectBinding: (id, bindingId) =>
    api.delete(`/clusters/${id}/projects/bindings/${encodeURIComponent(bindingId)}`),
  // Group grants: every member of a SarvKube Group gets the project; membership changes
  // on the Groups page re-sync it. See ProjectGroupAccess.js.
  listProjectGroupBindings: (id, projectId) =>
    api.get(`/clusters/${id}/projects/${projectId}/group-bindings`),
  addProjectGroupBinding: (id, projectId, data) =>
    api.post(`/clusters/${id}/projects/${projectId}/group-bindings`, data),
  syncProjectGroupBinding: (id, bindingId) =>
    api.post(`/clusters/${id}/projects/group-bindings/${bindingId}/sync`),
  removeProjectGroupBinding: (id, bindingId) =>
    api.delete(`/clusters/${id}/projects/group-bindings/${bindingId}`),
  // Group grants on ONE namespace, beside its Project — how one namespace reaches several
  // teams. `data.group_ids` grants several groups in one call.
  listNamespaceGroupBindings: (id, ns) =>
    api.get(`/clusters/${id}/namespaces/${encodeURIComponent(ns)}/group-bindings`),
  addNamespaceGroupBindings: (id, ns, data) =>
    api.post(`/clusters/${id}/namespaces/${encodeURIComponent(ns)}/group-bindings`, data),
  syncNamespaceGroupBinding: (id, bindingId) =>
    api.post(`/clusters/${id}/namespaces/group-bindings/${bindingId}/sync`),
  removeNamespaceGroupBinding: (id, bindingId) =>
    api.delete(`/clusters/${id}/namespaces/group-bindings/${bindingId}`),
  // Direct namespace grants (RoleBindings) — independent of Project membership, so a
  // namespace can be granted to more than one team at once. See NamespaceAccess.js.
  listNamespaceRoleBindings: (id, ns) =>
    api.get(`/clusters/${id}/namespaces/${encodeURIComponent(ns)}/role-bindings`),
  addNamespaceRoleBinding: (id, ns, data) =>
    api.post(`/clusters/${id}/namespaces/${encodeURIComponent(ns)}/role-bindings`, data),
  removeNamespaceRoleBinding: (id, ns, name) =>
    api.delete(`/clusters/${id}/namespaces/${encodeURIComponent(ns)}/role-bindings/${encodeURIComponent(name)}`),
  // Self-link: the calling user authenticates to Rancher once so SarvKube can act AS
  // them. Rancher will not let an admin mint a token on someone else's behalf.
  rancherLink: (id, data) => api.post(`/clusters/${id}/rancher-link`, data),

  getNetwork: (id) => api.get(`/clusters/${id}/network`),
  getStorage: (id) => api.get(`/clusters/${id}/storage`),
  getConfigResources: (id, ns) => api.get(`/clusters/${id}/config-resources`, { params: ns ? { namespace: ns } : {} }),
  nodeCapacity: (id) => api.get(`/clusters/${id}/nodes/capacity`),
  getDiagnostics: (id, params) => api.get(`/clusters/${id}/diagnostics`, { params }),
  // Node management
  listAllNodes: () => api.get('/clusters/nodes/all'),
  listNodes: (clusterId) => api.get(`/clusters/${clusterId}/nodes`),
  addNode: (clusterId, data) => api.post(`/clusters/${clusterId}/nodes`, data),
  updateNode: (clusterId, nodeId, data) => api.put(`/clusters/${clusterId}/nodes/${nodeId}`, data),
  deleteNode: (clusterId, nodeId) => api.delete(`/clusters/${clusterId}/nodes/${nodeId}`),
  provisionNode: (clusterId, nodeId) => api.post(`/clusters/${clusterId}/nodes/${nodeId}/provision`),
  // Node auto-discovery & SSH credentials
  syncNodes: (clusterId) => api.post(`/clusters/${clusterId}/sync-nodes`),
  updateClusterSsh: (clusterId, data) => api.put(`/clusters/${clusterId}/ssh`, data),
  updateNodeSsh: (clusterId, nodeId, data) => api.put(`/clusters/${clusterId}/nodes/${nodeId}/ssh`, data),
  // Agent connectivity
  getAgentManifest: (clusterId) => api.get(`/clusters/${clusterId}/agent-manifest`),
  getDaemonsetManifest: (clusterId) => api.get(`/clusters/${clusterId}/daemonset-manifest`),
  getAgentInstallCmd: (clusterId) => api.get(`/clusters/${clusterId}/agent-install`),
  getAgentStatus: (clusterId) => api.get(`/clusters/${clusterId}/agent-status`),
  // Set the cluster agent's scope: 'read' (default, view only) | 'write' (create/edit/delete workloads)
  updateAgentScope: (clusterId, scope) => api.post(`/clusters/${clusterId}/agent-scope`, null, { params: { scope } }),
  // Re-apply the current agent manifest (durable RBAC + resources); use after scope change / to upgrade agents
  redeployAgent: (clusterId) => api.post(`/clusters/${clusterId}/redeploy-agent`),
  // Pod logs streaming — returns {channel, ws_url}
  streamPodLogs: (clusterId, namespace, podName, params = {}) =>
    api.get(`/clusters/${clusterId}/pods/${namespace}/${podName}/logs`, { params }),
  // Deploy image
  deployImage: (clusterId, data) => api.post(`/clusters/${clusterId}/deploy`, data),
  // Exec kubectl command. Pass { section, action } so the backend enforces the
  // real RBAC section/verb for what this command actually does (create/delete a
  // Service, ConfigMap, etc.) instead of the generic 'workloads' view check.
  exec: (clusterId, command, { section, action } = {}) =>
    api.post(`/clusters/${clusterId}/exec`, { command, section, action }),
  // Scale / edit deployment
  patchDeployment: (clusterId, namespace, name, data) =>
    api.patch(`/clusters/${clusterId}/deployments/${namespace}/${name}`, data),
  // Rollback deployment
  rollbackDeployment: (clusterId, namespace, name, revision = null) =>
    api.post(`/clusters/${clusterId}/deployments/${namespace}/${name}/rollback`, revision ? { revision } : {}),
  // Deployment revision history
  deploymentHistory: (clusterId, namespace, name) =>
    api.get(`/clusters/${clusterId}/deployments/${namespace}/${name}/history`),
  // Delete a deployment directly from the cluster (not an AppService DB row)
  deleteDeployment: (clusterId, namespace, name) =>
    api.delete(`/clusters/${clusterId}/deployments/${namespace}/${name}`),
  // Rolling-restart a Deployment/StatefulSet/DaemonSet directly on the cluster, whether
  // or not SarvKube created it — `kind` is plural (deployments/statefulsets/daemonsets),
  // matching this page's own resource name in the URL.
  restartWorkload: (clusterId, kind, namespace, name) =>
    api.post(`/clusters/${clusterId}/${kind}/${namespace}/${name}/restart`),
  // Create namespace
  createNamespace: (clusterId, data) => api.post(`/clusters/${clusterId}/namespaces`, data),
  // Create/replace or remove a ResourceQuota on an EXISTING namespace — distinct from the
  // optional quota createNamespace can set up for a brand-new one.
  setResourceQuota: (clusterId, namespace, data) =>
    api.post(`/clusters/${clusterId}/namespaces/${encodeURIComponent(namespace)}/resourcequota`, data),
  deleteResourceQuota: (clusterId, namespace, name) =>
    api.delete(`/clusters/${clusterId}/namespaces/${encodeURIComponent(namespace)}/resourcequota/${encodeURIComponent(name)}`),
  // Join a worker node to the cluster (runs kubeadm join via SSH)
  joinWorkerNode: (clusterId, nodeId, data = {}) => api.post(`/clusters/${clusterId}/nodes/${nodeId}/join`, data),
  // Sprint 2 — Observability
  getNodeMetrics: (clusterId) => api.get(`/clusters/${clusterId}/metrics/nodes`),
  getPodMetrics: (clusterId, params = {}) => api.get(`/clusters/${clusterId}/metrics/pods`, { params }),
  getEvents: (clusterId, params = {}) => api.get(`/clusters/${clusterId}/events`, { params }),
};

export const observabilityAPI = {
  getAlertTrends: (days = 7) => api.get('/alerts/trends', { params: { days } }),
  searchAlerts: (params = {}) => api.get('/alerts/search', { params }),
};

// Deployments API
export const deploymentsAPI = {
  list: (clusterId = null) => {
    const params = clusterId ? { cluster_id: clusterId } : {};
    return api.get('/deployments/', { params });
  },
  get: (id) => api.get(`/deployments/${id}`),
  create: (data) => api.post('/deployments/', data),
  update: (id, data) => api.put(`/deployments/${id}`, data),
  delete: (id) => api.delete(`/deployments/${id}`),
};

// Pods API
export const podsAPI = {
  list: (clusterId = null, namespace = null) => {
    const params = {};
    if (clusterId) params.cluster_id = clusterId;
    if (namespace) params.namespace = namespace;
    return api.get('/pods/', { params });
  },
  get: (id) => api.get(`/pods/${id}`),
  create: (data) => api.post('/pods/', data),
  update: (id, data) => api.put(`/pods/${id}`, data),
  delete: (id) => api.delete(`/pods/${id}`),
  logs: (id) => api.get(`/pods/${id}/logs`),
};

// Alerts API
export const alertsAPI = {
  list: (filters = {}) => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.severity) params.severity = filters.severity;
    if (filters.cluster_name) params.cluster = filters.cluster_name;
    if (filters.namespace) params.namespace = filters.namespace;
    if (filters.alert_type) params.alert_type = filters.alert_type;
    if (filters.search) params.search = filters.search;
    if (filters.search_field && filters.search_field !== 'all') params.search_field = filters.search_field;
    if (filters.page) params.page = filters.page;
    if (filters.page_size) params.page_size = filters.page_size;
    return api.get('/alerts/', { params });
  },
  get: (alertId) => api.get(`/alerts/${alertId}`),
  getClusters: () => api.get('/alerts/clusters'),
  troubleshoot: (alertId) => api.post(`/alerts/${alertId}/troubleshoot`),
  resolve: (alertId, notes, rootCauseVerdict, rootCauseCorrection = null) => api.post(
    `/alerts/${alertId}/resolve`,
    rootCauseCorrection,
    { params: { resolution_notes: notes, root_cause_verdict: rootCauseVerdict } }
  ),
  getStats: () => api.get('/alerts/stats/summary'),
  // AI Analysis
  triggerAnalysis: (alertId, force = false) =>
    api.post(`/alerts/${alertId}/analyze`, null, { params: { force } }),
  getAnalysis: (alertId) => api.get(`/alerts/${alertId}/analysis`),
  getAnalysisStatus: (alertId) => api.get(`/alerts/${alertId}/analysis/status`),
  getActions: (alertId) => api.get(`/alerts/${alertId}/actions`),
  triggerBatchAnalysis: () => api.post('/alerts/batch/analyze'),
  // Logs
  uploadLogs: (alertId, logsData) => api.post(`/alerts/${alertId}/logs`, logsData),
  // Resolution Plan (Phase 2)
  generateResolutionPlan: (alertId, userContext = '', force = false) =>
    api.post(`/alerts/${alertId}/resolution-plan`, { user_context: userContext, force }),
  getResolutionPlan: (alertId) =>
    api.get(`/alerts/${alertId}/resolution-plan`),
  approveStep: (alertId, stepId, notes = '') =>
    api.post(`/alerts/${alertId}/resolution-plan/steps/${stepId}/approve`, { notes }),
  executeStep: (alertId, stepId) =>
    api.post(`/alerts/${alertId}/resolution-plan/steps/${stepId}/execute`),
  skipStep: (alertId, stepId) =>
    api.post(`/alerts/${alertId}/resolution-plan/steps/${stepId}/skip`),
};

// VPN Profiles API
export const vpnProfilesAPI = {
  list: () => api.get('/vpn-profiles/'),
  get: (id) => api.get(`/vpn-profiles/${id}`),
  create: (data) => api.post('/vpn-profiles/', data),
  update: (id, data) => api.put(`/vpn-profiles/${id}`, data),
  delete: (id) => api.delete(`/vpn-profiles/${id}`),
  test: (id) => api.post(`/vpn-profiles/${id}/test`),
  listClusters: (id) => api.get(`/vpn-profiles/${id}/clusters`),
};

export const playbooksAPI = {
  list:    ()          => api.get('/playbooks/'),
  get:     (id)        => api.get(`/playbooks/${id}`),
  create:  (data)      => api.post('/playbooks/', data),
  update:  (id, data)  => api.patch(`/playbooks/${id}`, data),
  delete:  (id)        => api.delete(`/playbooks/${id}`),
  preview: (params)    => api.get('/playbooks/match', { params }),
};

export const gpuAPI = {
  // GPU Servers
  listServers:     ()              => api.get('/gpu/'),
  registerServer:  (data)          => api.post('/gpu/', data),
  refreshMetrics:  (gpuServerId)   => api.post(`/gpu/${gpuServerId}/refresh`),
  deleteServer:    (gpuServerId)   => api.delete(`/gpu/${gpuServerId}`),
  releaseStuck:    (gpuServerId)   => api.post(`/gpu/${gpuServerId}/release-stuck`),
  // Instances
  listInstances:   (gpuServerId)   => api.get(`/gpu/${gpuServerId}/instances`),
  addInstance:     (gpuServerId, data) => api.post(`/gpu/${gpuServerId}/instances`, data),
  updateInstance:  (gpuServerId, instanceId, data) => api.patch(`/gpu/${gpuServerId}/instances/${instanceId}`, data),
  // Allocations
  allocate:        (gpuServerId, instanceId, data = {}) => api.post(`/gpu/${gpuServerId}/instances/${instanceId}/allocate`, data),
  myAllocations:   ()              => api.get('/gpu/allocations'),
  allAllocations:  ()              => api.get('/gpu/allocations/all'),
  assignAllocationOrg: (id, organizationId) => api.put(`/gpu/allocations/${id}/organization`, { organization_id: organizationId }),
  stopAllocation:  (id)            => api.post(`/gpu/allocations/${id}/stop`),
  startAllocation: (id)            => api.post(`/gpu/allocations/${id}/start`),
  terminateAlloc:  (id)            => api.delete(`/gpu/allocations/${id}`),
  // Quotas
  getQuota:        ()              => api.get('/gpu/quotas'),
  setQuota:        (data)          => api.put('/gpu/quotas', data),
  // Resource Requests
  submitRequest:   (data)          => api.post('/gpu/requests', data),
  listRequests:    ()              => api.get('/gpu/requests'),
  updateRequest:   (id, data)      => api.patch(`/gpu/requests/${id}`, data),
  provisionRequest:(id, data={})   => api.post(`/gpu/requests/${id}/provision`, data),
  getPodStatus:    (id)            => api.get(`/gpu/requests/${id}/pod-status`),
  // Direct GPU instance launch (no approval)
  launchInstance:  (data)          => api.post('/gpu/launch', data),
  // Resize/edit a provisioned instance (recreate pod, keep /app data)
  resizeInstance:  (id, data)      => api.post(`/gpu/requests/${id}/resize`, data),
  // Update the container image (recreate pod, keep the PVC; honest rollback on fail)
  updateImage:     (id, docker_image) => api.patch(`/gpu/requests/${id}/image`, { docker_image }),
  // Web terminal
  execInAllocation: (id, command)  => api.post(`/gpu/allocations/${id}/exec`, { command }),
  // Instance logs (kubectl logs via agent)
  allocationLogs:   (id, tail=200) => api.get(`/gpu/allocations/${id}/logs`, { params: { tail } }),
};

export const appServicesAPI = {
  list:       (clusterId, live=true) => api.get('/app-services/', { params: { ...(clusterId ? { cluster_id: clusterId } : {}), ...(live ? {} : { live: false }) } }),
  get:        (id, live=true) => api.get(`/app-services/${id}`, { params: live ? {} : { live: false } }),
  create:     (data)          => api.post('/app-services/', data),
  delete:     (id)            => api.delete(`/app-services/${id}`),
  liveStatus: (id)            => api.get(`/app-services/${id}/status`),
  scale:      (id, replicas)  => api.patch(`/app-services/${id}/scale`, { replicas }),
  restart:    (id)            => api.post(`/app-services/${id}/restart`),
  // Force-recreate ONE replica of a Deployment/StatefulSet (its controller recreates it
  // fresh) — restart() above only offers "all pods at once" via rollout restart.
  deletePod:  (id, podName)   => api.delete(`/app-services/${id}/pods/${encodeURIComponent(podName)}`),
  getLogs:        (id, podName, tail=100, previous=false) => api.get(`/app-services/${id}/logs`, { params: { pod_name: podName, tail, previous } }),
  getResources:   (id)                  => api.get(`/app-services/${id}/resources`),
  execInService:  (id, command)         => api.post(`/app-services/${id}/exec`, { command }),
  // Rolling-update a Deployment/StatefulSet to a new image version (no downtime).
  // For raw Pods this 409s — use restart (which recreates from svc.image) instead.
  updateImage:    (id, image)           => api.patch(`/workload-ops/${id}/image`, { image }),
  getManifestHistory: (id)              => api.get(`/app-services/${id}/manifests`),
};

export const groupsAPI = {
  list:               ()           => api.get('/groups/'),
  create:             (data)       => api.post('/groups/', data),
  get:                (id)         => api.get(`/groups/${id}`),
  update:             (id, data)   => api.put(`/groups/${id}`, data),
  delete:             (id)         => api.delete(`/groups/${id}`),
  assignMembers:      (id, userIds)    => api.put(`/groups/${id}/members`,     { user_ids: userIds }),
  assignPermissions:  (id, payload)   => api.put(`/groups/${id}/permissions`,  Array.isArray(payload) ? { sections: payload } : payload),
  assignClusters:     (id, clusterIds) => api.put(`/groups/${id}/clusters`,    { cluster_ids: clusterIds }),
};

export default api;
